import { IInternalNotesFilters } from '@sdk/contracts';
import { Badge, Button, Input, Popover } from 'antd';
import { Dayjs } from 'dayjs';
import { debounce } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultTheme, useTheme } from 'styled-components';

import { FiltersItems } from './FiltersItems';
import {
  BoldWrapper,
  ButtonsWrapper,
  ButtonWrapper,
  CloseHighlightedNotesWrapper,
  HighlightedNotesWrapper,
  TitleWrapper,
  Wrapper
} from './InternalNotesFilters.styles';

import { Icon } from '@/components/Icon';
import { useStudentInternalNotesList } from '@/store';

type Props = {
  onCreateClick: () => void;
  studentId?: string;
  studentName?: string;
};

export const InternalNotesFilters: React.FC<Props> = ({
  onCreateClick,
  studentId,
  studentName
}) => {
  const { t } = useTranslation('ChatTranslations');
  const { internalNotes, filters, setFilters, resetFilters, filtersCount, setSearch } =
    useStudentInternalNotesList(studentId);
  const { colors } = useTheme();

  const notesCount = useMemo(() => internalNotes?.length || 0, [internalNotes]);

  const highlightedNotesCount = useMemo(
    () => internalNotes?.filter(note => note.highlightedBy).length || 0,
    [internalNotes]
  );

  const clearFilter = (key: keyof IInternalNotesFilters) => {
    const updatedFilters = { ...filters };
    delete updatedFilters[key];
    setFilters({ ...updatedFilters });
  };

  const handleSetFilters = (
    key: keyof IInternalNotesFilters,
    value: boolean | string[] | [Dayjs, Dayjs]
  ) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      clearFilter(key);
    } else {
      setFilters({ ...filters, [key]: value });
    }
  };

  const handleNoteSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value === '') return setSearch('');
    if (value.length < 3) return;
    setSearch(value);
  };

  const handleSearch = debounce(handleNoteSearch, 500);

  return (
    <Wrapper>
      {filters.isHighlighted ? (
        <CloseHighlightedNotesWrapper>
          <Button
            shape="circle"
            size="small"
            type="link"
            icon={<Icon i="Close" />}
            onClick={() => handleSetFilters('isHighlighted', false)}
          />
          <BoldWrapper>{t('HIGHLIGHTED_NOTES')}</BoldWrapper>
        </CloseHighlightedNotesWrapper>
      ) : (
        <TitleWrapper>
          <div>{t('STUDENT_NOTE_COUNT', { count: notesCount, name: studentName })}</div>
          <Button type="primary" onClick={onCreateClick}>
            {t('CREATE_NOTE')}
          </Button>
        </TitleWrapper>
      )}
      <ButtonsWrapper>
        <Input.Search placeholder={t('SEARCH')} onChange={handleSearch} allowClear />
        <Popover
          trigger="hover"
          content={
            <FiltersItems
              filters={filters}
              filtersCount={filtersCount}
              resetFilters={resetFilters}
              handleSetFilters={handleSetFilters}
            />
          }
          placement="bottomRight">
          <ButtonWrapper>
            <Icon i="Filter" />
            {t('FILTERS')}
            {filtersCount > 0 && <Badge color={colors.green7} count={filtersCount} />}
          </ButtonWrapper>
        </Popover>
      </ButtonsWrapper>
      {highlightedNotesCount > 0 && (
        <HighlightedNotesWrapper onClick={() => handleSetFilters('isHighlighted', true)}>
          <Icon i="StarFilled" color={colors.green6 as keyof DefaultTheme['colors']} />
          <div>{t('HIGHLIGHTED_NOTES_COUNT', { count: highlightedNotesCount })}</div>
        </HighlightedNotesWrapper>
      )}
    </Wrapper>
  );
};
